/**
 * WarningGateDialog — Feb 2026.
 *
 * Shared confirm dialog that fires before ANY bulk action (Add to RFP,
 * change category, delete) when one or more selected contacts is flagged
 * `relationship_status === "warning"`. Lists all warning contacts by name +
 * reason and requires explicit confirm before the action proceeds.
 *
 * Usage: imperative-style helper via a returned trigger function. The
 * caller filters its selection through `gateWarningContacts(...)` and only
 * proceeds when the user confirms.
 *
 * NOTE: "caution"-status contacts do NOT trigger this gate — only "warning"
 * does. Caution is informational; warning blocks-without-confirm.
 *
 * data-testids:
 *   warning-gate-dialog
 *   warning-gate-confirm-btn
 *   warning-gate-cancel-btn
 *   warning-gate-contact-{contact_id}
 */

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertOctagon } from 'lucide-react';

export default function WarningGateDialog({
  open,
  onOpenChange,
  actionLabel,
  warningContacts,
  onConfirm,
  loading = false,
}) {
  if (!warningContacts || warningContacts.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="warning-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertOctagon className="w-5 h-5" />
            Include warning contact{warningContacts.length === 1 ? '' : 's'}?
          </DialogTitle>
          <DialogDescription>
            The following {warningContacts.length === 1 ? 'contact is' : `${warningContacts.length} contacts are`} flagged
            as <span className="font-medium text-red-700">warning</span>. Confirm explicitly to include
            {' '}{warningContacts.length === 1 ? 'them' : 'all of them'} in this action.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 max-h-60 overflow-y-auto border rounded-md divide-y bg-red-50/50">
          {warningContacts.map((c) => {
            const name = c.first_name
              ? `${c.first_name} ${c.last_name || ''}`.trim()
              : c.name || 'Unknown';
            const reason = c.relationship_status_reason;
            return (
              <div
                key={c.id}
                className="p-2.5 text-sm"
                data-testid={`warning-gate-contact-${c.id}`}
              >
                <div className="font-medium text-red-900">{name}</div>
                {c.company && <div className="text-xs text-red-700/70">{c.company}</div>}
                {reason && (
                  <div className="text-xs text-red-700 mt-1 italic">&ldquo;{reason}&rdquo;</div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="warning-gate-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="warning-gate-confirm-btn"
          >
            {loading
              ? 'Working…'
              : `Include anyway and ${actionLabel}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
